import { useState, type FormEvent } from 'react';
import { formatManilaDate, formatInclusiveManilaPeriodEnd } from '@dispatchdash/shared';
import {
  commissionApi,
  type CommissionSettlementRun,
  type CommissionSettlementRunDetail,
  type SalesAgentSummary,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useConfirm, useToast } from '@/components/Feedback';
import { formatPeso, humanize } from '@/lib/format';
import { adminNavItems } from '@/pages/navItems';

function currentMonthBounds() {
  const now = new Date();
  const manilaYmd = formatManilaDate(now);
  const year = Number(manilaYmd.slice(0, 4));
  const month = Number(manilaYmd.slice(5, 7));
  const pad = (n: number) => String(n).padStart(2, '0');
  const periodStart = `${year}-${pad(month)}-01`;
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const periodEnd = `${year}-${pad(month)}-${pad(lastDay)}`;
  return { periodStart, periodEnd };
}

function Money({ value }: { value: string | number | null | undefined }) {
  return <span className="money-cell">{formatPeso(value)}</span>;
}

function runPeriod(row: CommissionSettlementRun) {
  return (
    row.period ??
    `${formatManilaDate(row.periodStart)} → ${formatInclusiveManilaPeriodEnd(row.periodEnd, row.periodStart)}`
  );
}

function formatRate(rate?: string | null) {
  if (rate === null || rate === undefined || rate === '') return null;
  const n = Number(rate);
  if (!Number.isFinite(n)) return null;
  return `${Number((n * 100).toFixed(4))}%`;
}

export function AdminCommissionSettlement() {
  const confirm = useConfirm();
  const toast = useToast();
  const { data: runs, loading, error: runsLoadError, reload } = useAsync(() => commissionApi.listSettlementRuns(), []);
  const { data: agents } = useAsync(() => commissionApi.listAgents(), []);

  const [showRun, setShowRun] = useState(false);
  const [agentId, setAgentId] = useState('');
  const bounds = currentMonthBounds();
  const [periodStart, setPeriodStart] = useState(bounds.periodStart);
  const [periodEnd, setPeriodEnd] = useState(bounds.periodEnd);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [heldNotice, setHeldNotice] = useState('');

  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CommissionSettlementRunDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [finalizing, setFinalizing] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [rowDiscardingId, setRowDiscardingId] = useState<string | null>(null);
  const [pageError, setPageError] = useState('');

  const agentById = new Map((agents ?? []).map((a: SalesAgentSummary) => [a.id, a]));
  const agentName = (id: string) => agentById.get(id)?.personName ?? 'this agent';

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
    setDetailError('');
  };

  const confirmDiscard = (run: CommissionSettlementRun) =>
    confirm({
      title: 'Discard draft settlement?',
      message: `Discard the draft settlement for ${agentName(run.agentId)}, ${runPeriod(run)} (net payable ${formatPeso(run.netPayable)}). Its commission entries go back to pending so you can run the settlement again.`,
      confirmLabel: 'Discard draft',
      danger: true,
    });

  const loadDetail = async (id: string) => {
    setDetailId(id);
    setDetailLoading(true);
    setDetailError('');
    try {
      setDetail(await commissionApi.getSettlementRun(id));
    } catch (err) {
      setDetailError((err as { message?: string }).message ?? 'Failed to load settlement detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRunSettlement = async (e: FormEvent) => {
    e.preventDefault();
    if (!agentId) {
      setError('Select an agent');
      return;
    }
    setSaving(true);
    setError('');
    setHeldNotice('');
    try {
      const result = await commissionApi.runSettlement({
        agentId,
        // Manila calendar bounds — avoid UTC T23:59:59 which reads as next day in PH.
        periodStart: new Date(`${periodStart}T00:00:00+08:00`).toISOString(),
        periodEnd: new Date(`${periodEnd}T23:59:59.999+08:00`).toISOString(),
      });
      toast(`Settlement run for ${agentName(agentId)}: net payable ${formatPeso(result.run?.netPayable)}`);
      if (result.heldForCollectionCount) {
        setHeldNotice(
          `${result.heldForCollectionCount} entries held for pay-on-collection — they'll settle once their invoices are collected.`,
        );
      }
      setShowRun(false);
      reload();
      if (result.run?.id) void loadDetail(result.run.id);
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Failed to run settlement');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!detailId || !detail) return;
    const run = detail.run;
    const ok = await confirm({
      title: 'Finalise settlement?',
      message: `Finalise ${agentName(run.agentId)}'s settlement for ${runPeriod(run)}: ${detail.entries.length} entries, commission ${formatPeso(run.commissionTotal)} + bonus ${formatPeso(run.bonusTotal)} = ${formatPeso(run.netPayable)} net payable. It is locked afterwards and cannot be discarded.`,
      confirmLabel: 'Finalise settlement',
    });
    if (!ok) return;
    setFinalizing(true);
    setDetailError('');
    try {
      await commissionApi.finalizeSettlementRun(detailId);
      toast(`Settlement for ${agentName(run.agentId)} finalised`);
      await Promise.all([reload(), loadDetail(detailId)]);
    } catch (err) {
      setDetailError((err as { message?: string }).message ?? 'Failed to finalise settlement run');
    } finally {
      setFinalizing(false);
    }
  };

  const handleDiscard = async () => {
    if (!detailId || !detail) return;
    if (!(await confirmDiscard(detail.run))) return;
    setDiscarding(true);
    setDetailError('');
    try {
      await commissionApi.discardSettlementRun(detailId);
      toast(`Draft settlement for ${agentName(detail.run.agentId)} discarded`);
      closeDetail();
      reload();
    } catch (err) {
      setDetailError((err as { message?: string }).message ?? 'Failed to discard settlement run');
    } finally {
      setDiscarding(false);
    }
  };

  const columns = [
    {
      key: 'agentId',
      header: 'Agent',
      value: (row: CommissionSettlementRun) => agentById.get(row.agentId)?.personName ?? '',
      render: (row: CommissionSettlementRun) => agentById.get(row.agentId)?.personName ?? '—',
    },
    {
      key: 'periodStart',
      header: 'Period',
      value: (row: CommissionSettlementRun) => row.periodStart,
      render: (row: CommissionSettlementRun) => runPeriod(row),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row: CommissionSettlementRun) => <StatusBadge status={row.status} size="sm" />,
    },
    {
      key: 'commissionTotal',
      header: 'Commission',
      align: 'right' as const,
      value: (row: CommissionSettlementRun) => Number(row.commissionTotal),
      render: (row: CommissionSettlementRun) => <Money value={row.commissionTotal} />,
    },
    {
      key: 'bonusTotal',
      header: 'Bonus',
      align: 'right' as const,
      value: (row: CommissionSettlementRun) => Number(row.bonusTotal),
      render: (row: CommissionSettlementRun) => <Money value={row.bonusTotal} />,
    },
    {
      key: 'netPayable',
      header: 'Net payable',
      align: 'right' as const,
      value: (row: CommissionSettlementRun) => Number(row.netPayable),
      render: (row: CommissionSettlementRun) => <Money value={row.netPayable} />,
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      render: (row: CommissionSettlementRun) => (
        <div className="row" style={{ gap: 'var(--space-xs)' }}>
          <Button type="button" size="sm" variant="secondary" onClick={() => loadDetail(row.id)}>View</Button>
          {(row.canDiscard ?? (row.status === 'draft' && !row.finalizedAt)) && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              loading={rowDiscardingId === row.id}
              onClick={async () => {
                if (!(await confirmDiscard(row))) return;
                setRowDiscardingId(row.id);
                setPageError('');
                try {
                  await commissionApi.discardSettlementRun(row.id);
                  toast(`Draft settlement for ${agentName(row.agentId)} discarded`);
                  if (detailId === row.id) closeDetail();
                  reload();
                } catch (err) {
                  setPageError((err as { message?: string }).message ?? 'Failed to discard settlement run');
                } finally {
                  setRowDiscardingId(null);
                }
              }}
            >
              Discard
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <h1>Commission settlement</h1>
            <p className="text-muted">Run and finalise monthly commission settlements per agent</p>
          </div>
          <Button type="button" onClick={() => { setError(''); setHeldNotice(''); setShowRun(true); }}>Run settlement</Button>
        </div>

        {heldNotice && (
          <p className="text-muted" style={{ marginBottom: 'var(--space-md)' }}>{heldNotice}</p>
        )}
        {pageError && (
          <p className="form-error" style={{ marginBottom: 'var(--space-md)' }}>{pageError}</p>
        )}
        <DataTable
          columns={columns}
          data={runs ?? []}
          keyField="id"
          loading={loading}
          loadError={runsLoadError ? `Could not load settlement runs: ${runsLoadError}` : null}
          onRetry={() => void reload()}
          dense
          searchPlaceholder="Filter settlement runs…"
          emptyMessage="No settlement runs yet. Use “Run settlement” to calculate an agent’s commission for a period."
        />

        <Modal open={showRun} onClose={() => setShowRun(false)} title="Run commission settlement">
          <form onSubmit={handleRunSettlement}>
            {error && <div className="form-error" style={{ marginBottom: 'var(--space-md)' }}>{error}</div>}
            <Select label="Agent" value={agentId} onChange={(e) => setAgentId(e.target.value)} required>
              <option value="">Select agent…</option>
              {(agents ?? []).map((a) => (
                <option key={a.id} value={a.id}>{a.personName} ({humanize(a.engagementLevel)})</option>
              ))}
            </Select>
            <Input
              label="Period start"
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              required
            />
            <Input
              label="Period end"
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              required
            />
            <Button type="submit" loading={saving}>Run settlement</Button>
          </form>
        </Modal>

        <Modal
          open={detailId !== null}
          onClose={closeDetail}
          title={detail ? `Settlement · ${agentById.get(detail.run.agentId)?.personName ?? 'Agent'} · ${runPeriod(detail.run)}` : 'Settlement detail'}
          size="lg"
        >
          {detailLoading && !detail && <p className="text-muted">Loading…</p>}
          {detailError && <p className="form-error" style={{ marginBottom: 'var(--space-md)' }}>{detailError}</p>}
          {detail && (
            <div>
              <div className="grid-3" style={{ marginBottom: 'var(--space-lg)' }}>
                <div className="stat-block">
                  <span className="stat-value">{formatPeso(detail.run.commissionTotal)}</span>
                  <span className="stat-label">Commission</span>
                </div>
                <div className="stat-block">
                  <span className="stat-value">{formatPeso(detail.run.bonusTotal)}</span>
                  <span className="stat-label">Bonus</span>
                </div>
                <div className="stat-block">
                  <span className="stat-value">{formatPeso(detail.run.netPayable)}</span>
                  <span className="stat-label">Net payable</span>
                </div>
              </div>

              <div className="row-between" style={{ marginBottom: 'var(--space-sm)' }}>
                <h3>Entries ({detail.entries.length})</h3>
                <div className="row" style={{ gap: 'var(--space-sm)' }}>
                  {(detail.run.canDiscard ?? (!detail.run.finalizedAt && detail.run.status === 'draft')) && (
                    <Button type="button" size="sm" variant="secondary" loading={discarding} onClick={handleDiscard}>
                      Discard draft
                    </Button>
                  )}
                  {(detail.run.canFinalize ?? !detail.run.finalizedAt) && (
                    <Button type="button" size="sm" loading={finalizing} onClick={handleFinalize}>Finalise</Button>
                  )}
                </div>
              </div>
              {detail.entries.map((entry) => (
                <div key={entry.id} className="frozen-line-row">
                  <span>
                    <strong>{entry.tripNumber ?? 'Trip'}</strong>
                    {entry.clientName ? ` · ${entry.clientName}` : ''}
                    <span className="frozen-line-sub">
                      Revenue {formatPeso(entry.tripRevenue)} · Gross profit {formatPeso(entry.grossProfit)}
                      {formatRate(entry.applicableRate) ? ` × ${formatRate(entry.applicableRate)}` : ''}
                    </span>
                  </span>
                  <span className="money-cell">{formatPeso(entry.amount)}</span>
                </div>
              ))}

              {detail.bonusAwards.length > 0 && (
                <>
                  <h3 style={{ margin: 'var(--space-lg) 0 var(--space-sm)' }}>Bonus awards</h3>
                  {detail.bonusAwards.map((award) => (
                    <div key={award.id} className="frozen-line-row">
                      <span>{award.ruleLabel}</span>
                      <span className="money-cell">{formatPeso(award.amount)}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </Modal>
      </div>
      <style>{`
        .money-cell {
          display: block;
          text-align: right;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .frozen-line-row {
          display: flex;
          justify-content: space-between;
          padding: var(--space-sm) 0;
          border-bottom: 1px solid var(--color-border);
          font-size: 0.875rem;
          gap: var(--space-md);
        }
        .frozen-line-sub {
          display: block;
          color: var(--color-text-muted);
          font-size: 0.8125rem;
        }
        .form-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          font-size: 0.875rem;
        }
      `}</style>
    </Layout>
  );
}
